import React, { useEffect, useMemo, useState } from "react";

import _, { set } from "lodash";
import cx from "classnames";

import styles from "./organization.module.scss";
import Button from "src/components/button";
import { SimulationContext } from "../../../context";
import { ChatContainer } from "../../chat";

import TextareaAutosize from "react-autosize-textarea";
import { OrganizationInfo, Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { useDispatch } from "react-redux";
import { generateSimulationSummary, invalidateGenerateSimulationSummaryState, updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import { Input } from "src/components/input";
import ScrollToBottom from 'react-scroll-to-bottom';
import Tooltip from "src/components/tooltip";
import { useSelector } from "react-redux";
import { prepareOrganizationRequest, validateSimulationRequest } from "../../../helper";
import { ErrorMessage } from "../../../../components/error";

interface OrganizationEditorProps {
  simulation: Simulation;
}

export const OrganizationEditor: React.FC<OrganizationEditorProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = React.useContext(SimulationContext);

  const persistedOrganization = simulation?.organization;
  const [organization, setOrganization] = useState<OrganizationInfo | undefined>(persistedOrganization);
  const [errors, setErrors] = useState<SimulationValidationError[]>([]);

  const name = organization?.name || "";
  const description = organization?.description || "";

  const industry = organization?.industry || "";
  const revenue = organization?.revenue || "";
  const employees = organization?.employees || "";
  const tools_used = organization?.tools_used || "";

  const title = useMemo(() => _.get(simulation, 'description'), [simulation])

  const validationErrors = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.edit.errors`) as SimulationValidationError[];
  }, _.isEqual);

  const { generatingSummary, generatedSummary } = useSelector((state: any) => {
    return {
      generatingSummary: _.get(state, `coaching.simulations.edit.generateSummary.generating`, false),
      generatedSummary: _.get(state, `coaching.simulations.edit.generateSummary.generated`, false),
    }
  }, _.isEqual);

  const nameError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "name" }) || null;
  }, [errors]);

  const descriptionError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "description" }) || null;
  }, [errors]);

  const industryError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "industry" }) || null;
  }, [errors]);

  const revenueError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "revenue" }) || null;
  }, [errors]);

  const employeesError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "employees" }) || null;
  }, [errors]);

  const toolsUsedError = useMemo(() => {
    return _.find(errors, { type: "organization", property: "tools_used" }) || null;
  }, [errors]);


  useEffect(() => {
    setOrganization(persistedOrganization);
  }, [persistedOrganization]);

  useEffect(() => {
    if (organization) {
      const simulationRequest = {
        ...simulation,
        organization: organization
      }

      const validationErrors = validateSimulationRequest(simulationRequest);
      setErrors(validationErrors);
    }
  }, [organization]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  useEffect(() => {
    if (generatedSummary) {
      dispatch(invalidateGenerateSimulationSummaryState(simulation.id!));

      if (simulationContext.editOrganizationInfo) {
        simulationContext.editOrganizationInfo(false);
      }
    }
  }, [generatedSummary]);

  const handlePropertyChange = (property: string, value: any) => {
    setOrganization((prevOrganization) => {
      const updatedOrganization = {
        ...prevOrganization || {},
        [property]: value
      } as OrganizationInfo;
      return updatedOrganization;
    });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handlePropertyChange("name", event.target.value);
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("description", event.target.value);
  }

  const handleIndustryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("industry", event.target.value);
  }

  const handleRevenueChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("revenue", event.target.value);
  }

  const handleEmployeesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("employees", event.target.value);
  }

  const handleToolsUsedChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("tools_used", event.target.value);
  }

  const handleConfirm = () => {
    const changes = prepareOrganizationRequest({
      name: "",
      description: "",
      industry: "",
      revenue: "",
      employees: "",
      tools_used: "",
      summary: "",
    }, organization);

    dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "organization", value: organization }));

    dispatch(generateSimulationSummary({
      simulation: simulation.id!,
      description: simulation.description || "",
      organization: changes
    }));
  }
  const handleCancel = () => {
    if (simulationContext.editOrganizationInfo) {
      simulationContext.editOrganizationInfo(false);
    }
  }

  const handleGetParameters = () => {
    const parameters = {
      name: name,
      description: description,
      industry: industry,
      revenue: revenue,
      employees: employees,
      tools_used: tools_used
    };

    return parameters;
  }

  const handleParametersChange = (parameters: any) => {
    _.keys(parameters).forEach((key) => {
      const value = parameters[key];
      if (value) {
        handlePropertyChange(`${key}`, value);
      }
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title} onClick={handleCancel}>
          <button className={styles.backButton}>
            <i className={cx(styles.backIcon, 'paperflite-left-arrow')} />
          </button>
          <h2>Scenario</h2>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" className={cx(styles.action, styles.confirm)}
            icon="paperflite-tick" text={generatingSummary ? "Confirming..." : "Confirm"}
            disabled={generatingSummary}
            onClick={handleConfirm} />
          <Button className={cx(styles.action, styles.cancel)}
            icon="paperflite-cancel"
            text="Cancel"
            disabled={generatingSummary}
            onClick={handleCancel}
          />
        </div>
      </div>
      <p className={styles.description}>Edit your scenario</p>

      <div className={styles.body}>
        <ScrollToBottom className={cx(styles.left, styles.chat)} scrollViewClassName={styles.scrollView} followButtonClassName={styles.scrollButton}>
          <div className={styles.header}>
            <h4>Generate an organization profile with seek</h4>
          </div>
          <div className={styles.chats}>
            <ChatContainer simulation={simulation}
              configurationType="organization"
              getParameters={handleGetParameters}
              onChange={handleParametersChange}
            />
          </div>
        </ScrollToBottom>
        <div className={cx(styles.right, styles.organization)}>
          <div className={styles.summaryView}>
            <div className={styles.field}>
              <Input className={styles.name}
                value={name}
                placeholder="Enter organization name..."
                onChange={handleNameChange}
              />
              <ErrorMessage error={nameError} />
            </div>
            <div className={styles.field}>
              <TextareaAutosize
                className={styles.summary}
                rows={6}
                value={description}
                placeholder={'Type here the description...'}
                spellCheck={true}
                onChange={handleDescriptionChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              <ErrorMessage error={descriptionError} />
            </div>
          </div>
          <div className={styles.formView}>
            <div className={styles.field}>
              <FieldLabel label="Industry" error={industryError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={industry}
                placeholder={'Type here the industry...'}
                spellCheck={true}
                onChange={handleIndustryChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="# of Employees" error={employeesError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={employees}
                placeholder={'Type here the industry...'}
                spellCheck={true}
                onChange={handleEmployeesChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Revenue" error={revenueError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={revenue}
                placeholder={'Type here the revenue...'}
                spellCheck={true}
                onChange={handleRevenueChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Tech Stack / Tools Used" error={toolsUsedError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={tools_used}
                placeholder={'Type here the tools used...'}
                spellCheck={true}
                onChange={handleToolsUsedChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldLabelProps {
  label: string;
  error?: SimulationValidationError | null;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ label, error }) => {
  return (
    <Tooltip placement="top-start" content={error?.message} disabled={!error}>
      <label className={cx({ [styles.error]: error })}>
        {error && <i className={cx("fa-solid", "fa-triangle-exclamation")}></i>}
        {label}
      </label>
    </Tooltip>
  );
}